import { styled } from '@mui/material/styles';
import { css, keyframes } from '@emotion/react';
import { scrollbar, paddingRight, paddingLeft } from 'utils/styles';

/* Sweep matches the Figma export (kf_126_1656_translate_0): a 2s
   ease-out translate of (414.66px, 94.41px), scaled down to this
   button's size and padded with an idle hold so it repeats every few
   seconds instead of running back-to-back. */
const chatbotSkeletonAnimation = keyframes`
  0% {
    animation-timing-function: ease-out;
    translate: 0px 0px;
  }
  100% {
    translate: 400px 68px;
  }
`;

export const StyledLayoutSidebar = styled('div')(
  () => css`
    .sidebar-toggle-btn {
      position: fixed;
      top: 70px;
      z-index: 10;
      border: 1px solid #eeeeee;
      border-radius: 50%;
      cursor: pointer;
      margin-top: 8px;
      box-shadow: 4px 4px 4px 0px #0000000a;

      .icon-wrapper {
        width: 32px;
        height: 32px;
        background-color: #ffffff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &:hover {
        color: #37aaaf;
        border: 1px solid #37aaaf;
      }
    }

    .sidebar-toggle-btn.expanded {
      right: 240px;
    }

    .sidebar-toggle-btn.collapsed {
      right: 60px;
    }
    .layout-sidebar-sticky {
      background-color: white;
    }

    .layout-sidebar {
      width: 258px;
      /**
       * Mobile browsers size 100vh as if their toolbars were hidden, which
       * pushes the bottom-pinned Dastan row under the browser chrome.
       * 100dvh tracks the visible viewport; 100vh stays as the fallback
       * for browsers without dynamic viewport units.
       */
      height: 100vh;
      height: 100dvh;
      display: flex;
      flex-direction: column;
      background: #ffffff;
      padding-bottom: 20px;
      position: fixed;
      top: 0px;

      .mobile-menu-logo {
        display: none;
      }
      .logo {
        text-align: center;
        height: 70px;
        .app-logo {
          max-height: 54px;
        }
      }

      .nav-section {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
      }

      .chatbot-fixed-list {
        flex-shrink: 0;
      }

      .navbar {
        list-style: none;
        padding: 0;
        margin: 0;
        .nav-item,
        .sub-menu {
          padding-right: 25px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          margin-bottom: 21px;
          .menu {
            a {
              flex: 1;
              align-items: center;
            }
            position: relative;
            height: 35px;
            &.disabled {
              opacity: 0.3;
            }
            .arrow-down {
              margin-left: 20px;
              transition: all 0.3s;
              &.open {
                transform: rotate(180deg);
              }
            }
            .title {
              cursor: pointer;
              transition: all 0.6s;
              margin-right: 20px;
              font-style: normal;
              font-weight: 700;
              font-size: 1rem;
              color: #425466;
              flex: 1;
              white-space: nowrap;
            }
            a {
              display: flex;
            }
            &.active {
              .title {
                color: #199da3;
              }
              .icon {
                color: #199da3;
              }
            }
            &:hover {
              .title,
              .icon {
                color: #199da3;
              }
            }
          }
          &:last-child {
            margin-bottom: 0px;
          }
          &.active {
            position: relative;
            .title {
              color: #199da3;
            }
            .icon {
              color: #199da3;
            }
          }
          .navbar {
            margin-top: 20px;
            margin-bottom: 0px;
          }
        }

        .nav-item,
        .menu {
          a {
            flex: 1;
          }
          &.active {
            &:after {
              position: absolute;
              width: 4px;
              height: 36px;
              content: '';
              top: 0;
              right: -26px;
              background-color: #199da3;
            }
          }
        }
      }
      &.close {
        width: 80px;
        .app-logo {
          height: 49px;
        }
        .navbar {
          .nav-item {
            padding-right: 25px;
            .title {
              transition: all 0.4s;
              opacity: 0;
              font-size: 0;
            }

            .arrow-down {
              display: none;
            }
          }

          /**
           * Dastan is the exception in the collapsed rail: it's icon-only
           * (no wordmark) and its divider spans the full width, so it opts
           * out of the padding every other nav-item gets.
           */
          .nav-item:has(.chatbot-menu) {
            padding-right: 0;

            .chatbot-btn {
              box-sizing: border-box;
              padding: 16px 8px;
              border-radius: 6px;
              margin: 12px;
              padding: 0;
              height: 56px;

              .chatbot-icon-wrapper .chatbot-icon {
                width: 28px;
                height: 28px;
              }

              .chatbot-text {
                display: none;
              }
            }
          }
        }
      }
      hr {
        border: none;
        border: 1px solid rgba(160, 174, 192, 0.2);
        width: 80%;
        margin-top: 18px;
      }

      /* Dastan is the last row and owns its own height, unlike the 35px nav rows */
      .navbar .nav-item:last-child {
        margin-bottom: 0;
      }

      /**
       * Dastan opts out of the padding every other nav-item gets: the
       * collapsed-rail divider spans the full sidebar width, and the
       * expanded card insets itself with its own margin instead.
       */
      .navbar .nav-item:has(.chatbot-menu) {
        padding-right: 0;
      }

      /**
       * Collapsed rail: icon-only column layout (the .close override above
       * hides the wordmark). The expanded override below turns it into the
       * row-with-text footer card instead. Scoped through .menu so it
       * outranks the generic nav-item menu rule, which would otherwise
       * clamp the row to 35px.
       */
      .navbar .nav-item .menu.chatbot-menu {
        width: auto;
        height: auto;

        .chatbot-btn {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          padding: 12px;
          cursor: pointer;
          width: 100%;
          overflow: hidden;
          /* Same card look collapsed and expanded — without it the white
             sweep below has nothing but the sidebar's own white background
             to show against, so it renders invisibly. */
          background-color: #f7f8f9;
          border-radius: 8px;

          > * {
            position: relative;
            z-index: 1;
          }

          &::after {
            content: '';
            position: absolute;
            top: -180px;
            left: -50px;
            width: 130px;
            height: 400%;
            background: linear-gradient(
              90deg,
              transparent,
              transparent 35%,
              #ffffff 50%,
              transparent 65%,
              transparent
            );
            rotate: 45deg;
            /* forwards holds the sweep at its swept-away end position once the
               2 iterations finish, instead of snapping back to the 0% frame
               (which sits inside the card and would show as a stuck corner). */
            animation: ${chatbotSkeletonAnimation} 3s ease-out 2 forwards;
          }
        }

        .chatbot-icon-wrapper {
          order: 1;
          display: flex;
          align-items: center;
          justify-content: center;

          .chatbot-icon {
            display: block;
            width: 32px;
            height: 32px;
          }
        }

        .chatbot-text {
          order: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          min-width: 0;
        }

        .title {
          margin: 0 !important;
          color: #00426c;
          font-weight: 700;
          line-height: 36px;
          font-size: 20px;
          font-style: normal;
          cursor: pointer;
        }

        .subtitle {
          display: none;
        }

        &.expanded-chatbot {
          .chatbot-btn {
            flex-direction: row;
            align-items: center;
            gap: 12px;
            ${paddingRight('16px')}
            ${paddingLeft('12px')}
            padding-top: 12px;
            padding-bottom: 12px;
            margin: 12px;
          }

          .chatbot-icon-wrapper {
            .chatbot-icon {
              width: 36px;
              height: 36px;
            }
          }

          .chatbot-text {
            flex: 1;
            /* Cross-axis start on a column flex container is the inline-start
               side, which is the right in this RTL app */
            align-items: flex-start;
            gap: 8px;
          }

          .title {
            font-size: 18px !important;
            line-height: normal;
          }

          .subtitle {
            display: block;
            width: 100%;
            font-size: 12px;
            line-height: 20px;
            font-weight: 400;
            color: #0c395a;
            text-align: start;
          }
        }
      }
    }

    @media (max-width: 960px) {
      .sidebar-toggle-btn {
        display: none !important;
      }
      .layout-sidebar {
        background: #ffffff;
        position: fixed !important;
        z-index: 18;
        width: 260px;
        transform: translateX(100%);
        transition: transform 0.3s cubic-bezier(0.47, 0.3, 0.49, 0.85);

        &.animation-in {
          transform: translateX(0%);
        }

        .app-logo {
          display: none;
        }

        .mobile-menu-logo {
          display: block;
        }

        .nav-section {
          padding-bottom: 1rem;
          ${scrollbar(0, '#e3e7e7', '#e3e7e7')};
          &:hover {
            ${scrollbar(0, '#aac4c4', '#e3e7e7')};
          }
        }

        .navbar {
          padding-right: 0;
          margin-top: 0;
          -webkit-tap-highlight-color: transparent;
          -webkit-touch-callout: none;
          user-select: none;

          .nav-item {
            .menu {
              span,
              .icon {
                color: #199da3;
              }

              .title {
                color: #199da3;
                font-weight: 700;
                font-size: 1rem;
              }

              &.active {
                .title,
                .icon {
                  font-weight: 800;
                  color: #222222;
                }

                &:after {
                  background-color: #199da3;
                  right: 10px !important;
                }
              }
            }

            /**
             * The parent nav-item and the sub-menu already add 25px each, which
             * together give the original 50px indent, so the inner menu adds none.
             */
            .sub-menu {
              .menu {
                padding-right: 0;
              }

              &.active {
                .title {
                  font-weight: 800;
                  color: #222222;
                }
              }
            }

            &.active {
              .title,
              .icon {
                color: #222222;
                font-weight: 800;
              }

              &:after {
                background-color: #199da3;
                right: 10px !important;
              }
            }
          }
          .nav-item,
          .sub-menu {
            .menu {
              &:hover {
                .title,
                .icon {
                  color: #222222;
                }
              }
            }
          }
        }

        &.close {
          width: 260px;
          .navbar {
            .nav-item {
              .title {
                transition: unset;
                opacity: 1;
                font-size: 1rem;
              }

              .arrow-down {
                display: block;
              }
            }
          }
        }
      }
    }
  `
);
